import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

@observer
class ArticleFilterBar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showAllTags: false,
            showAuthorDropdown: false,
        };
    }

    onTagClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { tag } = e.target.closest('span').dataset;
        const { onTagToggle } = this.props;
        if (onTagToggle) {
            onTagToggle(tag);
        }
    }

    onAuthorClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { userId } = e.target.closest('span, button, div').dataset;
        const { onAuthorSelect } = this.props;
        if (onAuthorSelect) {
            onAuthorSelect(userId ? parseInt(userId, 10) : null);
        }
        this.setState({ showAuthorDropdown: false });
    }

    toggleAuthorDropdown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { showAuthorDropdown } = this.state;
        this.setState({ showAuthorDropdown: !showAuthorDropdown });
    }

    closeDropdown = () => {
        this.setState({ showAuthorDropdown: false });
    }

    componentDidMount() {
        document.addEventListener('click', this.closeDropdown);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.closeDropdown);
    }

    onClearFilters = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { onClearFilters } = this.props;
        if (onClearFilters) {
            onClearFilters();
        }
    }

    toggleShowAllTags = (e) => {
        e.preventDefault();
        const { showAllTags } = this.state;
        this.setState({ showAllTags: !showAllTags });
    }

    render() {
        const { showAllTags, showAuthorDropdown } = this.state;
        const {
            availableTags = [],
            selectedTags = [],
            creators = [],
            selectedAuthor,
            darkmode,
            showAuthorFilter = true,
        } = this.props;

        // Sort tags: selected first, then alphabetically
        const sortedTags = [...availableTags].sort((a, b) => {
            const aSelected = selectedTags.includes(a.name);
            const bSelected = selectedTags.includes(b.name);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return a.name.localeCompare(b.name);
        });

        // Limit displayed tags unless showAllTags is true
        const maxVisibleTags = 12;
        const visibleTags = showAllTags ? sortedTags : sortedTags.slice(0, maxVisibleTags);
        const hasMoreTags = sortedTags.length > maxVisibleTags;

        const hasFilters = selectedTags.length > 0 || selectedAuthor;
        const tagClass = darkmode ? 'dark' : 'light';

        // Deduplicate creators by id
        const uniqueCreators = creators.reduce((acc, creator) => {
            if (creator.id && !acc.find(c => c.id === creator.id)) {
                acc.push(creator);
            }
            return acc;
        }, []);

        const selectedCreator = uniqueCreators.find(c => c.id === selectedAuthor);

        if (availableTags.length === 0 && uniqueCreators.length === 0) {
            return null;
        }

        return (
            <div class='d-flex flex-wrap align-items-center gap-2 mb-1'>
                {/* Author filter */}
                {showAuthorFilter && uniqueCreators.length > 0 && (
                    <div class='d-flex align-items-center'>
                        <small class='text-muted me-2'>
                            <i class='fa-solid fa-user me-1' />
                            <Text id='article.filter-by-author'>Author:</Text>
                        </small>
                        {selectedAuthor ? (
                            <span
                                class='badge text-bg-primary'
                                style='cursor: pointer;'
                                onClick={this.onAuthorClick}
                                data-user-id=''
                                title='Click to clear author filter'
                            >
                                <i class='fa-solid fa-times me-1' />
                                {selectedCreator ? `${selectedCreator.firstname || ''} ${selectedCreator.lastname || ''}`.trim() : `User ${selectedAuthor}`}
                            </span>
                        ) : (
                            <div class='dropdown position-relative'>
                                <button
                                    class={`btn btn-sm btn-outline-secondary dropdown-toggle`}
                                    type='button'
                                    onClick={this.toggleAuthorDropdown}
                                    style='font-size: 0.75rem;'
                                >
                                    <Text id='article.all-authors'>All authors</Text>
                                </button>
                                {showAuthorDropdown && (
                                    <ul
                                        class='dropdown-menu show'
                                        style='max-height: 300px; overflow-y: auto; position: absolute; top: 100%; left: 0; z-index: 1000;'
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {uniqueCreators.map(creator => (
                                            <li key={creator.id}>
                                                <div
                                                    class='dropdown-item'
                                                    style='cursor: pointer;'
                                                    onClick={this.onAuthorClick}
                                                    data-user-id={creator.id}
                                                >
                                                    {creator.images?.[0]?.s3SmallLink ? (
                                                        <img
                                                            src={creator.images[0].s3SmallLink}
                                                            class='rounded-circle me-2'
                                                            style='width: 24px; height: 24px; object-fit: cover;'
                                                            alt={`${creator.firstname || ''} ${creator.lastname || ''}`}
                                                        />
                                                    ) : (
                                                        <i class='fa-solid fa-user-circle me-2 text-muted' />
                                                    )}
                                                    {`${creator.firstname || ''} ${creator.lastname || ''}`.trim() || `User ${creator.id}`}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Tags section */}
                {availableTags.length > 0 && (
                    <div class='d-flex flex-wrap align-items-center gap-1'>
                        {visibleTags.map(tag => {
                            const isSelected = selectedTags.includes(tag.name);
                            const isAiTag = tag.source === 'ai';
                            return (
                                <span
                                    key={tag.name}
                                    class={`badge ${isSelected ? 'text-bg-primary' : `text-bg-${tagClass}`}`}
                                    style='cursor: pointer; font-size: 0.7rem;'
                                    onClick={this.onTagClick}
                                    data-tag={tag.name}
                                    title={isAiTag ? 'AI-generated tag' : 'User tag'}
                                >
                                    {isSelected && <i class='fa-solid fa-check me-1' />}
                                    #{tag.name}
                                </span>
                            );
                        })}
                        {hasMoreTags && (
                            <button
                                type='button'
                                class='btn btn-sm btn-link text-muted p-0'
                                onClick={this.toggleShowAllTags}
                                style='font-size: 0.7rem;'
                            >
                                {showAllTags ? (
                                    <><i class='fa-solid fa-chevron-up me-1' /></>
                                ) : (
                                    <>+{sortedTags.length - maxVisibleTags}</>
                                )}
                            </button>
                        )}
                    </div>
                )}

                {/* Clear filters button */}
                {hasFilters && (
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-danger'
                        onClick={this.onClearFilters}
                        style='font-size: 0.7rem; padding: 0.15rem 0.4rem;'
                    >
                        <i class='fa-solid fa-times' />
                    </button>
                )}
            </div>
        );
    }
}

export default ArticleFilterBar;
